import { useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  BUTTON_VARIANTS,
  FILE_UPLOAD_VARIANTS,
  VALID_FILE_MIME_TYPES,
} from "@miplatform/constants";
import { Box, Typography } from "@mui/material";
import { MiFileInput } from "./MiFileInput";

/**
 * MiFileUpload component allows users to upload files with various configurations.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.variant=FILE_UPLOAD_VARIANTS.TEXT] - The variant of the file upload component.
 * @param {string} [props.acceptableFormatText="Acceptable formats: png, jpg, gif, pdf, doc, xls, csv"] - Text to display acceptable file formats.
 * @param {boolean} [props.disabled=false] - If true, the component is disabled.
 * @param {boolean} [props.dragAndDrop=false] - If true, enables drag and drop functionality.
 * @param {Function} [props.onChange=() => null] - Callback function to handle file list changes.
 * @param {Function} [props.onDelete=() => null] - Callback function to handle file deletion.
 * @param {Array<Object>} [props.initialFiles=[]] - Initial list of files.
 * @param {Function} [props.onFilePreview] - Callback function to handle file preview.
 * @param {boolean} [props.multiple=true] - If true, allows multiple file uploads.
 * @param {Array<string>} [props.validFileTypes=VALID_FILE_MIME_TYPES] - Array of valid file types.
 * @param {boolean} [props.enableDocTypeDropdown=false] - If true, enables document type dropdown.
 * @param {Array<Object>} [props.docTypes=[]] - Array of document types for the dropdown.
 * @param {number} [props.maxFileSizeInMB=30] - Maximum file size in megabytes.
 * @param {string} [props.buttonClassName] - Custom CSS class for the upload button.
 * @param {Object} [props.buttonStartIcon] - Icon to display at the start of the upload button.
 * @param {string} [props.title] - Title text for the upload area.
 * @param {string} [props.testId] - Test identifier for testing purposes.
 * @param {Object} [props.style={}] - Custom inline styles to apply to the component.
 * @param {boolean} [props.showUploadButton] - If true, shows an upload button in drag and drop mode.
 *
 * @returns {JSX.Element} The rendered MiFileUpload component.
 */
export const MiDragAndDropFileUpload = ({
  variant = FILE_UPLOAD_VARIANTS.TEXT,
  acceptableFormatText = "Acceptable formats: png, jpg, gif, pdf, doc, xls, csv",
  disabled = false,
  onChange,
  multiple = true,
  validFileTypes = VALID_FILE_MIME_TYPES,
  enableDocTypeDropdown = false,
  maxFileSizeInMB = 30,
  buttonClassName,
  buttonStartIcon,
  title,
  testId,
  showUploadButton,
}) => {
  const fileInputRef = useRef(null);

  const { classes } = useStyles({ disabled });
  const [pasteError, setPasteError] = useState(false);

  const handlePaste = (e) => {
    if (e.clipboardData.files.length > 0) {
      const newFiles = Object.values(e.clipboardData.files).map((file) =>
        validateFile(file)
      );
      onChange(newFiles);
      setPasteError(false);
    } else {
      setPasteError(true);
    }
  };

  const handleAddFile = (e) => {
    e.preventDefault();
    setPasteError(false);

    const { files } = e.dataTransfer || e.target;

    const newFiles = Object.values(files).map((file) => validateFile(file));
    if (!multiple) {
      onChange(newFiles);
      return;
    }

    onChange(newFiles);
  };

  const validateFile = (file) => {
    const newFile = file;
    file.error = null;

    if (enableDocTypeDropdown && !file.documentType) {
      file.error = "Please select a document type.";
    }

    if (file.size > maxFileSizeInMB * 10 * 1024 * 1024) {
      file.error = `File must be less than ${maxFileSizeInMB}MB.`;
    }

    if (!validFileTypes.includes(file.type)) {
      file.error = "File is the wrong format.";
    }

    return newFile;
  };

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  const renderFileInput = (buttonVariant) => {
    return (
      <MiFileInput
        title={title}
        multiple={multiple}
        onChange={handleAddFile}
        customClassName={buttonClassName}
        startIcon={buttonStartIcon}
        testId={testId}
        variant={buttonVariant}
      />
    );
  };

  return (
    <>
      <Box
        className={[classes.dropBoxContainer, pasteError && classes.error]}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          handleAddFile(e);
        }}
        onPaste={handlePaste}
      >
        <Typography className={classes.dropFilesText}>
          Drag & drop files here
          {variant === FILE_UPLOAD_VARIANTS.TEXT && (
            <>
              &nbsp;or&nbsp;
              <input
                ref={fileInputRef}
                className={classes.fileInput}
                type="file"
                data-testid={`${testId}-input`}
                onChange={handleAddFile}
                multiple={multiple}
                accept={validFileTypes.join(",")}
              />
              <span>
                <button
                  data-testid={`${testId}-browse-files`}
                  className={classes.browseFilesText}
                  onClick={handleBrowseClick}
                  type="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleBrowseClick();
                    }
                  }}
                >
                  upload files
                </button>
              </span>
            </>
          )}
        </Typography>
        <Typography className={classes.fileTypeText}>
          {acceptableFormatText}
        </Typography>
        {showUploadButton && renderFileInput(BUTTON_VARIANTS.CONTAINED)}
      </Box>
      {pasteError && (
        <Typography variant="BD3" className={classes.errorMessage}>
          Could not complete the paste command because your clipboard is empty
        </Typography>
      )}
    </>
  );
};

const useStyles = makeStyles()((theme, { disabled }) => ({
  fileUpload: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
  },
  dropBoxContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    border: `1px dashed ${theme.palette.grey.darkAccent}`,
    padding: theme.spacing(4, 4, 2, 4),
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    pointerEvents: disabled ? "none" : "default",
    opacity: disabled ? 0.5 : 1,
    textAlign: "center",
    alignItems: "center",
    maxHeight: "50vh",
    overflowY: "auto",
  },
  error: {
    borderColor: theme.palette.error.main,
  },
  dragDropText: {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "50%",
  },
  dropFilesText: {
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.typography.pxToRem(14),
    display: "flex",
    flexDirection: "row",
  },
  browseFilesText: {
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "underline",
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    margin: 0,
  },
  fileTypeText: {
    fontSize: theme.typography.pxToRem(12),
    display: "flex",
    flexDirection: "row",
    maxWidth: 400,
    marginTop: theme.spacing(-2),
    color: theme.palette.grey.medium,
  },
  errorMessage: {
    color: theme.palette.error.main,
    padding: theme.spacing(1, 2),
  },
  fileInput: {
    display: "none",
  },
}));
